(() => {
  class CartComfortError extends Error {
    constructor(code, cart = null, uncertain = false) {
      super(code);
      this.code = code;
      this.cart = cart;
      this.uncertain = uncertain;
    }
  }
  class CartRejectedError extends Error {}
  async function postOutcome(io, path, body) {
    try { await io.post(path, body); return 'accepted'; }
    catch (error) { return error instanceof CartRejectedError ? 'rejected' : 'uncertain'; }
  }

  const properties = item => Object.fromEntries(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const variantId = item => String(item.variant_id || item.id);
  const identity = item => JSON.stringify([variantId(item), properties(item), String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  const group = (cart, signature) => cart.items.filter(item => identity(item) === signature);
  const quantity = items => items.reduce((total, item) => total + item.quantity, 0);
  const complex = item => Boolean(item.selling_plan_allocation || item.parent_relationship || item.item_components?.length || item.has_components || item.requires_components);
  const validCart = cart => Array.isArray(cart?.items) && Number.isInteger(cart.item_count) && Number.isFinite(cart.total_price);

  function unchangedOthers(plan, cart) {
    const summarize = state => {
      const totals = new Map();
      state.items.forEach(item => {
        const key = identity(item);
        if (key !== plan.sourceIdentity && key !== plan.targetIdentity) totals.set(key, (totals.get(key) || 0) + item.quantity);
      });
      return JSON.stringify([...totals].sort(([a], [b]) => a.localeCompare(b)));
    };
    return summarize(plan.before) === summarize(cart);
  }

  function planReplacement(cart, request) {
    if (!validCart(cart)) throw new CartComfortError('uncertain', null, true);
    const source = cart.items.find(item => item.key === request.key);
    if (!source || String(source.product_id) !== String(request.productId) || variantId(source) !== String(request.fromId) || source.quantity !== request.quantity) throw new CartComfortError('changed', cart);
    if (complex(source) || !Number.isSafeInteger(source.quantity) || source.quantity < 1) throw new CartComfortError('unsupported', cart);
    const candidate = request.variants.find(item => String(item.id) === String(request.toId));
    if (!candidate || !candidate.available || candidate.requiresComponents || candidate.requiresSellingPlan || String(candidate.id) === variantId(source)) throw new CartComfortError('unavailable', cart);
    const target = { variant_id: candidate.id, properties: properties(source) };
    const sourceIdentity = identity(source), targetIdentity = identity(target);
    const sourceLines = group(cart, sourceIdentity), targetLines = group(cart, targetIdentity);
    if (sourceLines.length !== 1 || targetLines.length > 1 || targetLines.some(complex)) throw new CartComfortError('unsupported', cart);
    const totalForVariant = quantity(cart.items.filter(item => variantId(item) === String(candidate.id))) + source.quantity;
    const min = Number(candidate.min || 1), increment = Number(candidate.increment || 1), max = candidate.max == null ? null : Number(candidate.max);
    if (totalForVariant < min || (totalForVariant - min) % increment !== 0 || (max !== null && totalForVariant > max)) throw new CartComfortError('quantity_rule', cart);
    return {
      before: cart, sourceIdentity, targetIdentity, sourceQuantity: source.quantity,
      baselineTargetQuantity: quantity(targetLines), targetId: String(candidate.id),
      add: { items: [{ id: candidate.id, quantity: source.quantity, properties: properties(source) }] },
    };
  }

  async function readConfirmed(io) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const cart = await io.read();
        if (validCart(cart)) return cart;
      } catch {}
    }
    throw new CartComfortError('uncertain', null, true);
  }

  async function rollbackReplacement(io, plan, cart, code) {
    const source = group(cart, plan.sourceIdentity), target = group(cart, plan.targetIdentity);
    const delta = quantity(target) - plan.baselineTargetQuantity;
    if (quantity(source) !== plan.sourceQuantity || source.length !== 1 || target.length !== 1 || delta <= 0 || delta > plan.sourceQuantity || !unchangedOthers(plan, cart)) throw new CartComfortError('review', cart, true);
    await postOutcome(io, 'cart/change.js', { id: target[0].key, quantity: plan.baselineTargetQuantity });
    const restored = await readConfirmed(io);
    if (quantity(group(restored, plan.sourceIdentity)) === plan.sourceQuantity && quantity(group(restored, plan.targetIdentity)) === plan.baselineTargetQuantity && unchangedOthers(plan, restored)) throw new CartComfortError(code, restored);
    throw new CartComfortError('review', restored, true);
  }

  async function replaceVariant(io, request) {
    const before = await readConfirmed(io);
    const plan = planReplacement(before, request);
    const addition = await postOutcome(io, 'cart/add.js', plan.add);
    const added = await readConfirmed(io);
    const oldLines = group(added, plan.sourceIdentity), newLines = group(added, plan.targetIdentity);
    const delta = quantity(newLines) - plan.baselineTargetQuantity;
    if (!unchangedOthers(plan, added) || oldLines.length !== 1 || quantity(oldLines) !== plan.sourceQuantity || newLines.length > 1 || delta < 0 || delta > plan.sourceQuantity) throw new CartComfortError('review', added, true);
    if (addition === 'uncertain' && delta !== plan.sourceQuantity) throw new CartComfortError('uncertain', added, true);
    if (delta === 0) throw new CartComfortError('unavailable', added);
    if (delta !== plan.sourceQuantity) return rollbackReplacement(io, plan, added, 'unavailable');
    const removal = await postOutcome(io, 'cart/change.js', { id: oldLines[0].key, quantity: 0 });
    const changed = await readConfirmed(io);
    if (quantity(group(changed, plan.sourceIdentity)) === 0 && quantity(group(changed, plan.targetIdentity)) === plan.baselineTargetQuantity + plan.sourceQuantity && unchangedOthers(plan, changed)) return changed;
    if (removal === 'uncertain') throw new CartComfortError('uncertain', changed, true);
    return rollbackReplacement(io, plan, changed, 'failed');
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { CartComfortError, CartRejectedError, identity, planReplacement, replaceVariant, validCart };
    return;
  }
  if (window.ZothyCartComfort) return;
  let busy = false, uncertain = false;
  const disabledBefore = new Map();
  const route = path => `${window.Shopify?.routes?.root || '/'}${path}`;
  const strings = () => {
    try { return JSON.parse(document.getElementById('ZothyCartComfortStrings')?.textContent || '{}'); } catch { return {}; }
  };
  const message = code => strings()[code] || strings().failed || '';
  const drawer = () => document.querySelector('cart-drawer');
  const checkoutSelector = '[name="checkout"], a[href*="/checkout"], [data-zothy-checkout]';
  const request = async (path, body) => {
    const response = await fetch(route(path), {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      signal: AbortSignal.timeout(body ? 18000 : 9000),
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      if ([400, 404, 422].includes(response.status) || [400, 404, 422].includes(Number(result.status))) throw new CartRejectedError('Cart request rejected');
      throw new Error('Cart request outcome unknown');
    }
    return result;
  };
  const io = { read: () => request('cart.js'), post: request };

  function showMessage(code, isError = false) {
    const box = drawer()?.querySelector('[data-zothy-cart-status]');
    if (box) {
      box.textContent = message(code);
      box.hidden = !box.textContent;
      box.setAttribute('role', isError ? 'alert' : 'status');
    }
    drawer()?.querySelector('[data-zothy-cart-recheck]')?.toggleAttribute('hidden', !uncertain);
  }

  function lockControls() {
    const root = drawer();
    if (root) {
      root.toggleAttribute('data-comfort-busy', busy);
      root.setAttribute('aria-busy', String(busy));
    }
    for (const [element, wasDisabled] of disabledBefore) {
      if (element.isConnected && !busy && !(uncertain && element.matches(checkoutSelector))) {
        element.disabled = wasDisabled;
        disabledBefore.delete(element);
      } else if (!element.isConnected) disabledBefore.delete(element);
    }
    const controls = [...document.querySelectorAll(checkoutSelector)];
    if (busy && root) controls.push(...root.querySelectorAll('input, select, textarea, button:not(.drawer__close):not([data-zothy-cart-recheck])'));
    controls.forEach(element => {
      if ((busy || uncertain && element.matches(checkoutSelector)) && 'disabled' in element) {
        if (!disabledBefore.has(element)) disabledBefore.set(element, element.disabled);
        element.disabled = true;
      }
    });
  }

  async function render(cart) {
    const url = new URL(route('cart'), window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(9000) });
    if (!response.ok) throw new Error('Cart sections unavailable');
    const sections = await response.json();
    if (typeof sections['cart-drawer'] !== 'string' || typeof sections['cart-icon-bubble'] !== 'string') throw new Error('Missing cart sections');
    const component = drawer();
    if (component?.renderContents) {
      const alreadyOpen = component.classList.contains('active');
      component.classList.toggle('is-empty', cart.item_count === 0);
      component.renderContents({ ...cart, sections });
      if (alreadyOpen) requestAnimationFrame(() => {
        if (!component.classList.contains('active')) return;
        const container = component.querySelector('.drawer__inner');
        const focus = container?.querySelector('[data-zothy-cart-variant]') || container?.querySelector('.drawer__close');
        if (container && focus) {
          if (typeof trapFocus === 'function') trapFocus(container, focus);
          else focus.focus();
        }
      });
    }
    lockControls();
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zothy-cart-comfort', cartData: cart });
    } catch {}
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  function nativeQuantityPending() {
    if (document.querySelector('.cart__items--disabled')) return true;
    return [...document.querySelectorAll('cart-items .quantity__input, cart-drawer-items .quantity__input')].some(input => input.value !== input.defaultValue);
  }

  async function run(action, success) {
    if (busy || uncertain) return;
    if (nativeQuantityPending()) { showMessage('quantity_pending', true); return; }
    busy = true;
    lockControls();
    showMessage('working');
    let finalMessage = success, failure = false;
    try {
      const cart = await action();
      await render(cart);
    } catch (error) {
      failure = true;
      uncertain = !(error instanceof CartComfortError) || error.uncertain;
      finalMessage = error instanceof CartComfortError ? error.code : 'uncertain';
      if (error.cart) {
        try { await render(error.cart); } catch { uncertain = true; finalMessage = 'uncertain'; }
      }
    } finally {
      busy = false;
      lockControls();
      showMessage(finalMessage, failure);
    }
  }

  document.addEventListener('change', event => {
    const select = event.target.closest?.('[data-zothy-cart-variant]');
    if (!select) return;
    event.stopImmediatePropagation();
    const selected = select.value;
    select.value = select.dataset.currentVariant;
    if (selected === select.dataset.currentVariant || busy || uncertain) return;
    let variants;
    try { variants = JSON.parse(select.parentElement.querySelector('[data-zothy-cart-variants]').textContent); } catch { return; }
    void run(() => replaceVariant(io, {
      key: select.dataset.lineKey, productId: select.dataset.productId, fromId: select.dataset.currentVariant,
      toId: selected, quantity: Number(select.dataset.quantity), variants,
    }), 'replaced');
  }, true);

  document.addEventListener('click', event => {
    const recheck = event.target.closest?.('[data-zothy-cart-recheck]');
    if (recheck) {
      event.preventDefault();
      if (busy) return;
      busy = true; lockControls(); showMessage('working');
      void readConfirmed(io).then(async cart => { await render(cart); uncertain = false; showMessage('refreshed'); })
        .catch(() => { uncertain = true; showMessage('uncertain', true); })
        .finally(() => { busy = false; lockControls(); });
      return;
    }
    const upsell = event.target.closest?.('[data-zothy-cart-upsell]');
    if (upsell) {
      event.preventDefault();
      if (busy || uncertain) return;
      void run(async () => {
        const before = await readConfirmed(io);
        if (before.items.some(item => String(item.product_id) === upsell.dataset.productId)) throw new CartComfortError('already_added', before);
        const addition = await postOutcome(io, 'cart/add.js', { items: [{ id: upsell.dataset.variantId, quantity: 1 }] });
        const cart = await readConfirmed(io);
        if (!cart.items.some(item => String(item.product_id) === upsell.dataset.productId && variantId(item) === upsell.dataset.variantId)) throw new CartComfortError(addition === 'uncertain' ? 'uncertain' : 'unavailable', cart, addition === 'uncertain');
        return cart;
      }, 'added');
      return;
    }
    if ((busy || uncertain) && event.target.closest?.(checkoutSelector) || busy && event.target.closest?.('cart-remove-button, quantity-input, cart-drawer .quantity__button')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if ((busy || uncertain) && (event.target.matches('form[action*="/cart"]') || event.submitter?.matches(checkoutSelector))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  const observe = () => {
    const component = drawer();
    if (component) new MutationObserver(() => { if (busy || uncertain) lockControls(); }).observe(component, { childList: true, subtree: true });
  };
  window.ZothyCartComfort = { isBusy: () => busy, needsReview: () => uncertain };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe, { once: true });
  else observe();
})();
